import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import isEmpty from 'lodash/isEmpty';
import { getPopupContent, getProperIconClassName, closeOpenedPopup } from '../helpers';
import stores from '../../../stores';
import { Legend } from './Legend';
import Loader from '../../loader';
import { TEXT_LAYERS } from './constants';
import { withLoadingFallback } from '../../../hocs/withLoadingFallback';

const LoaderContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 1;
  position: relative;
  background-color: #f9f9f9;
  opacity: 0.7;
`;

const Container = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
`;

const LoaderPlaceholder = withLoadingFallback(Fragment);

class MapClassComponent extends Component {
  map;
  fitMapToBounds = true;
  mapCurrentCenter;

  componentDidMount() {
    const { isMobileView, setMapObject, latitude, longitude } = this.props;

    this.map = new mapboxgl.Map({
      container: this.mapContainer,
      style: 'mapbox://styles/mapbox/outdoors-v11',
      minZoom: 0,
      maxZoom: 19,
      center: [longitude, latitude],
      zoom: 11,
      maxBounds: [
        [-180, -85],
        [180, 85],
      ],
      accessToken: process.env.MAPBOX_ACCESS_TOKEN,
      attributionControl: false,
      logoPosition: isMobileView ? 'bottom-left' : 'bottom-right',
    }).addControl(
      new mapboxgl.AttributionControl({
        compact: true,
      })
    );

    if (setMapObject) {
      setMapObject(this.map);
    }

    if (!isMobileView) {
      const nav = new mapboxgl.NavigationControl({ showCompass: false });
      this.map.addControl(nav, 'top-right');
    }

    this.map.on('load', () => {
      TEXT_LAYERS.forEach((el) => {
        this.map.setLayoutProperty(el, 'text-field', ['get', 'name_fr']);
      });

      const { examCentersData } = this.props;
      this.addExamCentersToMap(examCentersData);
      this.fitMapToBounds = false;
    });

    this.map.on('dragend', this.updateMarkers);
    this.map.on('zoomend', this.updateMarkers);
  }

  componentDidUpdate(prevProps) {
    const { examCentersData } = this.props;
    if (prevProps.examCentersData !== examCentersData) {
      this.addExamCentersToMap(examCentersData);
    }
  }

  updateMarkers = () => {
    const newCenter = this.map.getCenter();
    if (this.mapCurrentCenter) {
      if (
        newCenter.lat !== this.mapCurrentCenter.lat &&
        newCenter.lng !== this.mapCurrentCenter.lng
      ) {
        const { lat, lng } = newCenter;
        const { updateExamCentersByCoordinates } = this.props;
        updateExamCentersByCoordinates(lat, lng, this.getMapRadius());
      }
    }
  };

  getMapRadius = () => {
    let radius = 0;
    if (this.map) {
      const mapBounds = this.map.getBounds().toArray();
      radius = (turf.distance(mapBounds[0], mapBounds[1]) * 1000) / 2;
    }
    return radius;
  };

  addExamCentersToMap = (features) => {
    const markers = document.querySelectorAll('.mapboxgl-marker');
    markers.forEach((marker) => {
      marker.removeEventListener('click', this.handleMarkerClick);
      marker.remove();
    });

    closeOpenedPopup();

    this.displayMarkers(features);

    if (!isEmpty(features) && this.fitMapToBounds) {
      const bounds = new mapboxgl.LngLatBounds();

      features.forEach((feature) => {
        bounds.extend(feature.geometry.coordinates);
      });

      this.map.fitBounds(bounds, {
        padding: 40,
      });
    }

    this.mapCurrentCenter = this.map.getCenter();
  };

  displayMarkers = (features) => {
    const { map } = this;

    features.forEach((marker) => {
      const el = document.createElement('div');
      el.className = getProperIconClassName(marker);
      el.setAttribute('data-marker-id', marker.properties.id);
      el.addEventListener('click', this.handleMarkerClick);

      const popup_content = getPopupContent(marker.properties);

      new mapboxgl.Marker(el)
        .setLngLat(marker.geometry.coordinates)
        .setPopup(
          new mapboxgl.Popup({ offset: 25, className: 'vv-exam-map__popup' }).setHTML(popup_content)
        )
        .addTo(map);
    });
  };

  handleMarkerClick = (e) => {
    const targetId = e.target.getAttribute('data-marker-id');

    const elemRow = document.querySelector(`[data-row-id='${targetId}']`);
    elemRow.scrollIntoView({ behavior: 'smooth' });
  };

  render() {
    const { isDataLoading } = stores.examCentersSearchStore;
    const { isMobileView, isLegendVisible } = this.props;

    return (
      <Container
        ref={(el) => {
          this.mapContainer = el;
        }}
      >
        {isLegendVisible && <Legend isMobileView={isMobileView} />}
        <LoaderPlaceholder
          isLoading={isDataLoading}
          fallback={
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          }
        />
      </Container>
    );
  }
}

MapClassComponent.propTypes = {
  updateExamCentersByCoordinates: PropTypes.func.isRequired,
  examCentersData: PropTypes.array.isRequired,
  isMobileView: PropTypes.bool,
  setMapObject: PropTypes.func,
  latitude: PropTypes.string,
  longitude: PropTypes.string,
  isLegendVisible: PropTypes.bool,
};

MapClassComponent.defaultProps = {
  isMobileView: false,
  setMapObject: null,
  latitude: 49.023461463214126,
  longitude: 3.44970703125,
  isLegendVisible: true,
};

const Map = observer(MapClassComponent);

export { Map };
